import { AxiosError } from 'axios';
import { SearchIcon } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import Evidence from 'components/Modal/Evidence';
import { formatCPFCNPJ } from 'helpers/utils/cpfCnpjMask';
import { isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import moment from 'moment';
import {
  Button,
  ButtonThemes,
  Col,
  IconButton,
  Pagination,
  Row,
  Select,
  TableWithOverflow,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  PresenceElements,
  TypeOfCollection,
  useGetBranchAndNucleu,
  usePostPresencePages,
} from 'services/editVotes';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { ReactComponent as BiometricIcon } from './biometric.svg';
import { ReactComponent as FaceIcon } from './face.svg';

type OptionsType = { value: string; label: string };

const ListAssociate = () => {
  const [branch, setBranch] = useState<string | null>(null);
  const [nucleou, setNucleou] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const params = useParams();
  const dispatch = useDispatch();
  const { showToast } = useContext(LayoutContext);

  const {
    data: resp,
    isPending,
    mutate,
  } = usePostPresencePages(currentPage, pageSize);
  const data: any = resp?.data;

  const { data: branchAndNucleouOptions } = useGetBranchAndNucleu(
    (params as { id: string }).id,
    {
      enabled: true,
    }
  );

  const branchesOptions: OptionsType[] = branchAndNucleouOptions
    ? Object.keys(branchAndNucleouOptions)
        .sort()
        .map((key) => ({ value: key, label: key }))
    : [];

  const nucleouOptions: OptionsType[] =
    branch && branchAndNucleouOptions[branch]
      ? branchAndNucleouOptions[branch].map((value: any) => ({
          value,
          label: value,
        }))
      : [];

  const typeOfCollection = useCallback((type: TypeOfCollection) => {
    switch (type) {
      case TypeOfCollection.PHYSICAL:
        return 'Assinatura Física';
      case TypeOfCollection.BIOMETRIC:
        return 'Assinatura Biométrica';
      case TypeOfCollection.FACE:
        return 'Assinatura Facial';
      default:
        return '-';
    }
  }, []);

  const handleOpenEvidence = useCallback(
    (idConvocado: number, idEvento: number) => {
      const modalKey: string = getUniqueKey();

      return dispatch(
        openModal(Evidence, {
          idConvocado,
          idEvento,
          modalKey: modalKey,
          preventEscExit: true,
          preventMaskExit: false,
          mobileOnXS: false,
          onClose: () => closeModal(modalKey),
        })
      );
    },
    [dispatch]
  );

  const handleListAssociate = useCallback(
    (page: number, size: number) => {
      if (
        branchAndNucleouOptions &&
        Object.keys(branchAndNucleouOptions).length
      ) {
        mutate(
          {
            agencias: branch
              ? [branch]
              : Object.keys(branchAndNucleouOptions!)! ?? [],
            cpfOrNome: '',
            idEvento: Number((params as { id: string }).id),
            nucleos: nucleou ? [nucleou] : [],
            page: page,
            size: size,
          },
          {
            onSuccess: () => {},
            onError: (error: Error) => {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label:
                  translation.Toasts.Errors.Keys[
                    `${
                      (error as AxiosError<Error>).response?.data.message ||
                      (error as AxiosError<Error>).response?.data ||
                      (error as AxiosError<Error>).message
                    }` as keyof typeof translation.Toasts.Errors.Keys
                  ] ?? translation.Toasts.Errors.defaultAlternative,
              });
            },
          }
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [branchAndNucleouOptions, mutate, branch, params, nucleou, showToast]
  );

  useEffect(() => {
    if (
      branchAndNucleouOptions &&
      Object.keys(branchAndNucleouOptions).length
    ) {
      handleListAssociate(currentPage, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchAndNucleouOptions]);

  return (
    <Row>
      <Row>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Select
            value={branch as any}
            options={branchesOptions as any}
            placeholder={'Agência'}
            onChange={(option) => {
              setBranch(option);
              setNucleou(null);
            }}
          />
        </Col>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Select
            placeholder={'Núcleo'}
            value={nucleou as any}
            options={nucleouOptions}
            onChange={(option) => setNucleou(option)}
            disabled={!branch}
          />
        </Col>
        <Row>
          <Col end buttonActionsCol>
            <Button
              theme={ButtonThemes.Secondary}
              leftIcon={<TimesCircleIcon />}
              onClick={() => {
                setBranch(null);
                setNucleou(null);
              }}
              disabled={isNullOrUndefined(branch) && isNullOrUndefined(nucleou)}
            >
              Limpar campos
            </Button>
            <Button
              leftIcon={<SearchIcon />}
              onClick={() => {
                handleListAssociate(currentPage, pageSize);
              }}
              loading={isPending}
              disabled={isPending}
            >
              Filtrar
            </Button>
          </Col>
        </Row>
      </Row>
      <Row>
        <Col>
          <TableWithOverflow
            showTopNavigator={false}
            noResultMessage={'Não há associados para listar'}
            columns={[
              {
                key: 'name',
                props: {
                  align: 'left',
                },
                value: 'Nome',
              },
              {
                key: 'cpfCnpj',
                props: {
                  align: 'left',
                },
                value: 'CPF/CNPJ',
              },
              {
                key: 'account',
                props: {
                  align: 'left',
                },
                value: 'Conta',
              },
              {
                key: 'type',
                props: {
                  align: 'left',
                },
                value: 'Tipo de coleta',
              },
              {
                key: 'date',
                props: {
                  align: 'left',
                },
                value: 'Data e hora da coleta',
              },
              {
                key: 'coordinator',
                props: {
                  align: 'center',
                },
                value: 'Coordenadas',
              },
              {
                key: 'action',
                props: {
                  align: 'center',
                  style: {
                    width: '150px',
                  },
                },
                value: 'Evidência',
              },
            ]}
            lines={data?.elements.map((row: PresenceElements) => {
              return {
                key: row.oidConvocado,
                name:
                  row.nomeConvocado !== row.presencaConfirmadaPor ? (
                    <div>
                      <p>{row.nomeConvocado}</p>
                      <p
                        style={{
                          marginTop: '4px',
                          fontSize: '12px',
                          opacity: '0.8',
                        }}
                      >
                        Representante: {row.presencaConfirmadaPor}
                      </p>
                    </div>
                  ) : (
                    row.nomeConvocado
                  ),
                cpfCnpj:
                  row.cpfCnpj !== row.cpfCnpjConfirmadaPor ? (
                    <div>
                      <p>{formatCPFCNPJ(row.cpfCnpj)}</p>
                      <p
                        style={{
                          marginTop: '4px',
                          fontSize: '12px',
                          opacity: '0.8',
                        }}
                      >
                        Representante: {formatCPFCNPJ(row.cpfCnpjConfirmadaPor)}
                      </p>
                    </div>
                  ) : (
                    formatCPFCNPJ(row.cpfCnpj)
                  ),
                account: row.conta,
                type: typeOfCollection(row.tipoAssinatura),
                date: moment(row.dataHoraPresenca).format(
                  'DD/MM/yyyy - HH:mm:ss'
                ),
                coordinator:
                  row.latitude === '0' && row.longitude === '0' ? (
                    '-'
                  ) : (
                    <div>
                      <p>
                        {row.latitude},&nbsp;{row.longitude}
                      </p>
                    </div>
                  ),
                action:
                  row.tipoAssinatura !== TypeOfCollection.PHYSICAL ? (
                    <IconButton
                      tooltip-text={'Visualizar'}
                      icon={
                        row.tipoAssinatura === TypeOfCollection.BIOMETRIC ? (
                          <BiometricIcon />
                        ) : row.tipoAssinatura === TypeOfCollection.FACE ? (
                          <FaceIcon />
                        ) : (
                          <Fragment />
                        )
                      }
                      onClick={() =>
                        handleOpenEvidence(
                          row.oidConvocado,
                          Number((params as { id: string }).id)
                        )
                      }
                    />
                  ) : (
                    '-'
                  ),
              };
            })}
          />
        </Col>
        <Col>
          <Pagination
            totalPages={data?.totalPages}
            currentPage={currentPage}
            onPageChange={(_, val) => {
              setCurrentPage(val);
              setTimeout(() => {
                handleListAssociate(val, pageSize);
              }, 150);
            }}
            loading={isPending}
            disabled={isPending}
            rowsPerPage={{
              rows: pageSize,
              options: [10, 25, 50],
              onChange: (_, size) => {
                setPageSize(size);
                setCurrentPage(1);
                setTimeout(() => {
                  handleListAssociate(1, size);
                }, 150);
              },
            }}
            countPages={{
              rows: pageSize,
              totalItems: data!?.totalElements,
            }}
          />
        </Col>
      </Row>
    </Row>
  );
};

export default ListAssociate;
